"use client";

import { hashKey, useQueryClient, type InfiniteData, type QueryKey } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { useContext, useEffect, useRef } from "react";

import {
  CUSTOMER_WINDOW_REFRESH_MS,
  type CustomerWindowTab,
} from "@/lib/realtime/customer-projection/window-contract";

import { requestCustomerListRefresh } from "@/dashboard/features/hub/hooks/customer/use-customer-list-refresh-budget";
import type { CustomerPageResponse } from "@/dashboard/features/hub/lib/customer/schemas";
import { CustomerListWindowClient } from "@/dashboard/features/hub/realtime/customer-list-window-client";
import { CustomerListWindowOwnership } from "@/dashboard/features/hub/realtime/customer-list-window-ownership";
import { CustomerListWindowTransportContext } from "@/dashboard/features/hub/realtime/hub-realtime-status";

/** Give a visible first-page query temporary ownership to fresh authenticated windows. */
export function useCustomerListWindow({
  enabled,
  queryKey,
  tab,
  atTop,
}: {
  enabled: boolean;
  queryKey: QueryKey;
  tab: CustomerWindowTab | null;
  atTop: boolean;
}): void {
  const queryClient = useQueryClient();
  const transport = useContext(CustomerListWindowTransportContext);
  const minimumSourceReadAt = useRef(0);
  const queryHash = hashKey(queryKey);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    let pending = queryClient.isMutating() > 0;
    return queryClient.getMutationCache().subscribe(() => {
      const nextPending = queryClient.isMutating() > 0;
      if (pending && !nextPending) {
        minimumSourceReadAt.current = DateTime.utc().toMillis();
      }
      pending = nextPending;
    });
  }, [enabled, queryClient]);

  useEffect(() => {
    if (!enabled || !tab || !transport || !atTop) {
      return;
    }
    let disposed = false;
    let unsubscribe: (() => void) | null = null;
    let releaseOwnership: (() => void) | null = null;
    let client: CustomerListWindowClient | null = null;
    let expiryTimer: ReturnType<typeof setTimeout> | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let installEpoch = 0;
    let awaitingRecovery = false;

    const eligible = () => {
      const data = queryClient.getQueryData<InfiniteData<CustomerPageResponse>>(queryKey);
      return (
        !disposed &&
        document.visibilityState === "visible" &&
        queryClient.isMutating() === 0 &&
        data?.pages.length === 1 &&
        data.pageParams.length === 1 &&
        data.pageParams[0] === null
      );
    };
    const clearOwnership = () => {
      installEpoch++;
      releaseOwnership?.();
      releaseOwnership = null;
      if (expiryTimer) {
        clearTimeout(expiryTimer);
        expiryTimer = null;
      }
    };
    const stop = () => {
      clearOwnership();
      unsubscribe?.();
      unsubscribe = null;
      client = null;
    };
    const fallback = (resubscribe = false) => {
      clearOwnership();
      client?.reset();
      awaitingRecovery = !resubscribe;
      if (resubscribe) {
        stop();
        schedule();
      }
      void requestCustomerListRefresh(queryClient, queryKey, {
        enabled: true,
        reason: "resync",
        predicate: key => hashKey(key) === queryHash,
      });
    };
    const schedule = () => {
      if (!disposed && !retryTimer) {
        retryTimer = setTimeout(() => {
          retryTimer = null;
          reconcile();
        }, CUSTOMER_WINDOW_REFRESH_MS);
      }
    };
    const yieldOwnership = () => {
      awaitingRecovery = false;
      stop();
      schedule();
    };
    const accept = async (message: unknown, receiver: CustomerListWindowClient) => {
      if (receiver !== client || !eligible()) {
        return;
      }
      const result = receiver.accept(message);
      if (result.kind === "ignore") {
        return;
      }
      if (result.kind === "fallback") {
        fallback(result.resubscribe);
        return;
      }
      const epoch = ++installEpoch;
      try {
        await queryClient.cancelQueries({ queryKey, exact: true });
        if (epoch !== installEpoch || receiver !== client || !eligible()) {
          return;
        }
        if (!receiver.isFresh()) {
          fallback(true);
          return;
        }
        if (queryClient.getQueryState(queryKey)?.fetchStatus === "fetching") {
          yieldOwnership();
          return;
        }
        awaitingRecovery = false;
        queryClient.setQueryData<InfiniteData<CustomerPageResponse>>(queryKey, {
          pages: [result.page],
          pageParams: [null],
        });
        releaseOwnership?.();
        releaseOwnership = CustomerListWindowOwnership.register(queryClient, queryKey, {
          isFresh: () => eligible() && receiver === client && receiver.isFresh(),
          yield: yieldOwnership,
        });
        if (expiryTimer) {
          clearTimeout(expiryTimer);
        }
        expiryTimer = setTimeout(
          () => fallback(true),
          Math.max(
            0,
            DateTime.fromISO(result.window.leaseExpiresAt).toMillis() - DateTime.utc().toMillis()
          )
        );
      } catch {
        fallback(true);
      }
    };
    const reconcile = () => {
      if (!eligible()) {
        stop();
        return;
      }
      if (queryClient.getQueryState(queryKey)?.fetchStatus === "fetching") {
        if (!awaitingRecovery) {
          stop();
        }
        schedule();
        return;
      }
      if (unsubscribe) {
        return;
      }
      const subscriptionId = crypto.randomUUID();
      const receiver = new CustomerListWindowClient(
        subscriptionId,
        transport.authorization,
        new Set(transport.tenantIds),
        tab,
        minimumSourceReadAt.current
      );
      client = receiver;
      unsubscribe = transport.subscribe(
        subscriptionId,
        tab,
        message => void accept(message, receiver)
      );
    };
    const stopQuerySubscription = queryClient.getQueryCache().subscribe(event => {
      if (event.query.queryHash !== queryHash) {
        return;
      }
      if (event.query.state.fetchStatus === "fetching") {
        if (!awaitingRecovery) {
          yieldOwnership();
        }
      } else {
        schedule();
      }
    });
    const stopMutationSubscription = queryClient.getMutationCache().subscribe(() => {
      const pending = queryClient.isMutating() > 0;
      if (pending) {
        stop();
      } else {
        schedule();
      }
    });
    document.addEventListener("visibilitychange", reconcile);
    reconcile();
    return () => {
      const ownedWindow = !!releaseOwnership;
      disposed = true;
      stop();
      if (retryTimer) {
        clearTimeout(retryTimer);
      }
      stopQuerySubscription();
      stopMutationSubscription();
      document.removeEventListener("visibilitychange", reconcile);
      if (ownedWindow) {
        fallback();
      }
    };
    // The exact query hash is stable even when filters recreate their equivalent tuple.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [atTop, enabled, queryClient, queryHash, tab, transport]);
}
